import type { TFunction } from 'i18next';

import type { DuelHubResponse } from '../../../services/api';
import { duelText } from './duelsText';
import { formatScore } from './duelsTheme';

export type DuelHomeTarget = 'rewards' | 'duels' | 'season';

type Counts = {
	requestable: number;
	openRequests: number;
	recentlyFulfilled: number;
	claimable?: number;
} | null;

const timeLeft = (t: TFunction, iso: string, now: number) => {
	const ms = Math.max(0, new Date(iso).getTime() - now);
	const hours = Math.floor(ms / 3_600_000);
	return hours >= 1
		? duelText(t, 'T_SITE_DUELS_UNIT_HOURS', [hours])
		: duelText(t, 'T_SITE_DUELS_UNIT_MINUTES', [Math.max(1, Math.ceil(ms / 60_000))]);
};

export const duelHomeCard = (
	t: TFunction,
	hub: DuelHubResponse | null,
	counts: Counts,
	now = Date.now(),
): { meta: string; target: DuelHomeTarget } => {
	if (!hub) return { meta: '', target: 'duels' };

	if (counts?.requestable) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_REWARD_UNLOCKED'), target: 'rewards' };
	}
	if (counts?.claimable) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_CLAIM_REWARD'), target: 'season' };
	}
	if (counts?.openRequests) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_UNDER_REVIEW'), target: 'rewards' };
	}
	if (counts?.recentlyFulfilled) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_REWARD_SENT'), target: 'rewards' };
	}

	const current = hub.currentDuel;
	if (current && !current.currentPlayerFinished) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_IN_PROGRESS'), target: 'duels' };
	}
	if (current?.expiresAt) {
		return {
			meta: duelText(t, 'T_SITE_DUELS_HOME_RESULT_IN', [timeLeft(t, current.expiresAt, now)]),
			target: 'duels',
		};
	}
	if (!current && hub.pools.some(pool => pool.openPool)) {
		return { meta: duelText(t, 'T_SITE_DUELS_HOME_DUEL_READY'), target: 'duels' };
	}

	const next = hub.rating.nextDivision;
	if (next) {
		return {
			meta: duelText(t, 'T_SITE_DUELS_HOME_TO_NEXT', [
				formatScore(Math.max(0, next.minRating - hub.rating.rating)),
				next.name.toUpperCase(),
			]),
			target: 'duels',
		};
	}
	return { meta: hub.rating.division.label.toUpperCase(), target: 'duels' };
};
